import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Download, ExternalLink, Folder, FolderInput } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { resolvePreviewKind } from "@/design-system/lib/preview";
import { documentsApi } from "@/api/documents";
import { foldersApi } from "@/api/folders";
import { DocumentList } from "./DocumentList";
import { ArchiveDocumentModal } from "./ArchiveDocumentModal";
import { MoveDocumentModal } from "./MoveDocumentModal";
import { LinkedDocumentsSection } from "./LinkedDocumentsSection";
import { LinkRelationshipModal } from "./LinkRelationshipModal";
import { documentRelationshipsApi } from "@/api/documentRelationships";
import { BulkActionBar } from "@/components/ux/BulkActionBar";
import type { DocumentFilters } from "./DocumentFilterPanel";
import { countActiveFilters } from "./DocumentFilterPanel";
import { VersionHistoryPanel } from "./VersionHistoryPanel";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type { ArchiveScope, DocumentDto } from "@/types/api";
import { useDateFormatter } from "@/utils/datetime";

// Query-key prefixes every DMS read a document move can stale out. Held as
// module consts rather than inline literals so the keys this file invalidates
// stay registered in one place (the stale-after-save convention).
const FOLDER_DETAIL_KEY = ["dms", "folderDetail"] as const;
const FOLDER_DOCS_KEY = ["dms", "folderDocs"] as const;
const ENTITY_TREE_KEY = ["dms", "entityTree"] as const;
const FOLDER_PICKER_TREE_KEY = ["folder-picker-tree"] as const;

interface FolderContentsProps {
  folderId: string;
  onFolderSelect?: (id: string) => void;
  bucketIds?: string[];
  onToggleBucket?: (doc: DocumentDto) => void;
  archiveScope?: ArchiveScope;
  /** Page-level setter for the Show archived toggle. When provided the
   * archive success toast offers a "Show archived" action that flips it. */
  onShowArchivedRequested?: () => void;
  /** When provided, document rows are fetched from /api/documents with
   * these filter params instead of the folder-detail endpoint. Subfolders
   * + breadcrumb still come from the folder-detail endpoint either way. */
  filters?: DocumentFilters;
}

export function FolderContents({
  folderId,
  onFolderSelect,
  bucketIds,
  onToggleBucket,
  archiveScope = "ActiveOnly",
  onShowArchivedRequested,
  filters,
}: FolderContentsProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { hasPermission, user } = useAuth();
  const { dateTime, date } = useDateFormatter();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeDocument, setActiveDocument] = useState<DocumentDto | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<DocumentDto | null>(null);
  const [moveTarget, setMoveTarget] = useState<DocumentDto | null>(null);
  const [linkMenuOpen, setLinkMenuOpen] = useState(false);
  const [linkModal, setLinkModal] = useState<
    | { mode: "bulk"; documentIds: string[] }
    | { mode: "single"; seedDocumentId: string }
    | null
  >(null);

  // Folder-detail still drives subfolders + breadcrumb. We only pivot the
  // *document* fetch when the page is supplying filters — everywhere else
  // (EntityDocumentTab, etc.) keeps the single-call pattern unchanged.
  const detail = useQuery({
    queryKey: ["dms", "folderDetail", folderId, archiveScope],
    queryFn: () => foldersApi.detail(folderId, archiveScope),
  });

  const usingFilteredList = filters !== undefined;
  const filterKey = filters
    ? [
        filters.category ?? "",
        filters.ingestionStatus ?? "",
        filters.uploadedBy ?? "",
        filters.dateFrom ?? "",
        filters.dateTo ?? "",
        filters.tags ?? "",
      ].join("|")
    : "";

  const filteredList = useQuery({
    queryKey: ["dms", "folderDocs", folderId, archiveScope, filterKey],
    enabled: usingFilteredList,
    queryFn: () =>
      documentsApi.list({
        folderId,
        archiveScope,
        // The UI doesn't paginate today; cap high enough to cover any
        // realistic single-folder browse without forcing a redesign.
        pageSize: 1000,
        category: filters?.category,
        ingestionStatus: filters?.ingestionStatus,
        uploadedBy: filters?.uploadedBy,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
        tags: filters?.tags,
      }),
  });

  const docs: DocumentDto[] = usingFilteredList
    ? filteredList.data?.data ?? []
    : detail.data?.documents ?? [];
  const subfolders = detail.data?.childFolders ?? [];
  const docsLoading = usingFilteredList ? filteredList.isLoading : detail.isLoading;
  const docsError = usingFilteredList
    ? filteredList.isError
      ? (filteredList.error as Error).message
      : null
    : detail.isError
      ? (detail.error as Error).message
      : null;
  const activeFilterCount = filters ? countActiveFilters(filters) : 0;

  const selectedAllEligible = useMemo(() => {
    if (!bucketIds) return false;
    return docs
      .filter((d) => d.ingestionStatus === "Completed" || d.ingestionStatus === "Ingested")
      .every((d) => bucketIds.includes(d.id));
  }, [docs, bucketIds]);

  // Invalidate every cached scope variant ("ActiveOnly" / "All") so the
  // row leaves or re-enters whichever view the user is on, plus any
  // sibling tab still showing the old data.
  //
  // Deliberately BARE prefixes rather than folder-scoped ones: a move can now
  // land in another record entirely, and the destination's folder id isn't
  // known here — a folder-scoped invalidation would refresh the source and
  // leave the destination's document list, entity tree and picker counts
  // stale. React Query matches by prefix, so these cover every parameterised
  // variant (archive scope, filter key, owner type/id) under each root.
  const refreshFolder = () => {
    qc.invalidateQueries({ queryKey: FOLDER_DETAIL_KEY });
    qc.invalidateQueries({ queryKey: FOLDER_DOCS_KEY });
    qc.invalidateQueries({ queryKey: ENTITY_TREE_KEY });
    qc.invalidateQueries({ queryKey: FOLDER_PICKER_TREE_KEY });
  };

  const isArchived = (d: DocumentDto) =>
    Boolean(d.isArchived ?? d.archivedAt);

  const canArchive = (d: DocumentDto) =>
    hasPermission("Documents.Archive") || d.createdByUserId === user?.id;

  const canUnarchive = () => hasPermission("Documents.Unarchive");

  // Move is gated on Documents.Update (the controller's policy).
  const canMove = hasPermission("Documents.Update");

  // Root of the current folder's owning tree — the move picker's DEFAULT
  // anchor, not a boundary: it opens on this record, and a search pick
  // re-anchors it to another record's tree. Breadcrumb is root-first; fall
  // back to the folder itself when it has no ancestors (i.e. it is the root).
  const treeRootId = detail.data?.breadcrumb?.[0]?.id ?? folderId;
  // …and the record it belongs to, named for the cross-record warning. The
  // breadcrumb root's name IS the record's display name (entity roots are
  // named by the root-name resolver); fall back to the folder's own name.
  const treeRootName =
    detail.data?.breadcrumb?.[0]?.name ?? detail.data?.folder?.name ?? "this record";

  // Clicking a row toggles the slide-over: re-clicking the open document
  // closes it; any other row swaps the drawer's contents.
  const toggleDocument = (doc: DocumentDto) =>
    setActiveDocument((cur) => (cur?.id === doc.id ? null : doc));

  // Map of doc id -> title for the link modals (direction labels need it).
  const titlesById = useMemo(
    () => Object.fromEntries(docs.map((d) => [d.id, d.title])),
    [docs],
  );

  const afterLinkChange = () => {
    refreshFolder();
    qc.invalidateQueries({ queryKey: ["document-relationships"] });
  };

  const groupTogether = useMutation({
    mutationFn: (ids: string[]) => documentRelationshipsApi.create(ids, "GroupedWith"),
    onSuccess: (_data, ids) => {
      toast({ message: `Linked ${ids.length} documents.`, tone: "success" });
      setSelectedIds([]);
      setLinkMenuOpen(false);
      afterLinkChange();
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not link the documents.";
      toast({ message, tone: "error" });
    },
  });

  // Open a related document in the same drawer by fetching its full DTO.
  const openRelated = useMutation({
    mutationFn: (id: string) => documentsApi.get(id),
    onSuccess: (full) => setActiveDocument(full),
  });

  const unarchive = useMutation({
    mutationFn: (id: string) => documentsApi.unarchive(id),
    onSuccess: (updated) => {
      setActiveDocument(updated);
      refreshFolder();
      toast({
        message: `“${updated.title}” restored to ${updated.folderName ?? "its folder"}.`,
        tone: "success",
      });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not restore the document.";
      toast({ message, tone: "error" });
    },
  });

  return (
    <div>
      {subfolders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-space-2 mb-space-3">
          {subfolders.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onFolderSelect?.(f.id)}
              className="flex items-center gap-space-2 px-space-3 py-space-2 border border-border-light rounded-md hover:bg-warm-white text-left"
            >
              <Folder size={14} aria-hidden className="text-gold" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-small text-onyx truncate">{f.name}</div>
                <div className="text-caption text-gray-mid">
                  {f.childFolderCount} folders · {f.documentCount} docs
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {bucketIds && onToggleBucket && docs.length > 0 && (
        <div className="mb-space-2 text-caption text-gray-mid">
          {selectedAllEligible
            ? "All ingested documents in this folder are in the Working Bucket."
            : "Tick ingested documents to add them to the Working Bucket."}
        </div>
      )}

      {usingFilteredList && activeFilterCount > 0 && (
        <div className="mb-space-2 text-caption text-gray-mid">
          {docs.length === 0
            ? "No documents match the current filters."
            : `${docs.length} match${docs.length === 1 ? "" : "es"} for the current filters.`}
        </div>
      )}

      <DocumentList
        documents={docs}
        loading={docsLoading}
        error={docsError}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onSelectDocument={toggleDocument}
      />

      {selectedIds.length >= 2 && (
        <div className="sticky bottom-0 mt-space-3 z-10">
          <div className="relative">
            <BulkActionBar
              selectedIds={selectedIds}
              onClear={() => {
                setSelectedIds([]);
                setLinkMenuOpen(false);
              }}
              actions={[
                {
                  key: "link",
                  label: "Link",
                  primary: true,
                  onClick: () => setLinkMenuOpen((o) => !o),
                },
              ]}
            />
            {linkMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 bottom-full mb-space-1 min-w-[220px] bg-white-pure border border-border-light rounded-md shadow-md py-space-1"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm disabled:opacity-50"
                  disabled={groupTogether.isPending}
                  onClick={() => groupTogether.mutate(selectedIds)}
                >
                  Group together
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm"
                  onClick={() => {
                    setLinkMenuOpen(false);
                    setLinkModal({ mode: "bulk", documentIds: selectedIds });
                  }}
                >
                  Other relationship…
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ResourceDrawer
        open={!!activeDocument}
        onClose={() => setActiveDocument(null)}
        title={activeDocument?.title ?? ""}
        testId="document-detail-drawer"
      >
        {activeDocument && (
          <div>
            <DocumentFilePreview document={activeDocument} />
            {isArchived(activeDocument) && (
              <div className="mb-space-2">
                <ArchivedBadge />
              </div>
            )}
            <dl className="grid grid-cols-2 gap-y-space-2 text-small">
              <dt className="text-gray-mid">Category</dt>
              <dd>
                <Badge status="info">{activeDocument.category}</Badge>
              </dd>
              <dt className="text-gray-mid">Size</dt>
              <dd>{formatBytes(activeDocument.fileSizeBytes)}</dd>
              <dt className="text-gray-mid">Version</dt>
              <dd>v{activeDocument.version}</dd>
              <dt className="text-gray-mid">Uploaded</dt>
              <dd>{dateTime(activeDocument.createdAt)}</dd>
              <dt className="text-gray-mid">Ingestion</dt>
              <dd>{activeDocument.ingestionStatus}</dd>
            </dl>
            {isArchived(activeDocument) && (
              <div className="mt-space-3 pt-space-2 border-t border-border-light text-small">
                <div className="text-gray-mid">
                  Archived{" "}
                  {activeDocument.archivedAt ? date(activeDocument.archivedAt) : ""}
                  {activeDocument.archivedByDisplayName
                    ? ` by ${activeDocument.archivedByDisplayName}`
                    : ""}
                </div>
                {activeDocument.archiveReason && (
                  <div className="mt-space-1 text-onyx italic">
                    “{activeDocument.archiveReason}”
                  </div>
                )}
              </div>
            )}
            {activeDocument.ingestionStatus === "Failed" && activeDocument.ingestionErrorMessage && (
              <p className="text-small text-status-error mt-space-2 break-words">
                {activeDocument.ingestionErrorMessage}
              </p>
            )}
            {activeDocument.description && (
              <p className="text-small text-onyx mt-space-2">{activeDocument.description}</p>
            )}
            {activeDocument.tags && (
              <div className="mt-space-2 flex flex-wrap gap-space-1">
                {activeDocument.tags.split(",").map((t) => (
                  <Badge key={t.trim()} status="info">
                    {t.trim()}
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-space-2 mt-space-3">
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  const res = await documentsApi.download(activeDocument.id);
                  window.open(res.sasUrl, "_blank", "noopener");
                }}
              >
                Download
              </Button>
              {!isArchived(activeDocument) && canMove && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setMoveTarget(activeDocument)}
                >
                  <FolderInput size={14} aria-hidden /> Move…
                </Button>
              )}
              {!isArchived(activeDocument) && canArchive(activeDocument) && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setArchiveTarget(activeDocument)}
                >
                  <Archive size={14} aria-hidden /> Archive
                </Button>
              )}
              {isArchived(activeDocument) && canUnarchive() && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={unarchive.isPending}
                  onClick={() => unarchive.mutate(activeDocument.id)}
                >
                  <ArchiveRestore size={14} aria-hidden /> Restore
                </Button>
              )}
              {onToggleBucket && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={
                    activeDocument.ingestionStatus !== "Completed" &&
                    activeDocument.ingestionStatus !== "Ingested"
                  }
                  onClick={() => onToggleBucket(activeDocument)}
                >
                  {bucketIds?.includes(activeDocument.id)
                    ? "Remove from Bucket"
                    : "Add to Bucket"}
                </Button>
              )}
            </div>
            <LinkedDocumentsSection
              documentId={activeDocument.id}
              onOpenDocument={(id) => openRelated.mutate(id)}
              onLinkAnother={() =>
                setLinkModal({ mode: "single", seedDocumentId: activeDocument.id })
              }
              onUnlinked={afterLinkChange}
            />
            <VersionHistoryPanel documentId={activeDocument.id} />
          </div>
        )}
      </ResourceDrawer>

      <ArchiveDocumentModal
        document={archiveTarget}
        open={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onArchived={(updated) => {
          setActiveDocument(updated);
          refreshFolder();
        }}
        onShowArchivedRequested={onShowArchivedRequested}
      />

      <MoveDocumentModal
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        document={moveTarget}
        treeRootId={treeRootId}
        treeRootName={treeRootName}
        onMoved={(updated) => {
          setActiveDocument(updated);
          refreshFolder();
        }}
      />

      {linkModal && (
        <LinkRelationshipModal
          open
          onClose={() => setLinkModal(null)}
          mode={linkModal.mode}
          seedDocumentId={
            linkModal.mode === "single" ? linkModal.seedDocumentId : undefined
          }
          documentIds={
            linkModal.mode === "bulk" ? linkModal.documentIds : undefined
          }
          folderId={folderId}
          titlesById={titlesById}
          onLinked={() => {
            const linkedCount =
              linkModal.mode === "bulk" ? linkModal.documentIds.length : 2;
            toast({ message: `Linked ${linkedCount} documents.`, tone: "success" });
            setLinkModal(null);
            setSelectedIds([]);
            afterLinkChange();
          }}
        />
      )}
    </div>
  );
}

// Inline preview pane shown above the metadata in the detail slide-over.
// PDFs render in an <iframe>, images in an <img>, text in a capped <pre>;
// Office docs and any other type fall back to Download / Open in new tab.
// Reuses the existing signed-URL endpoint (documentsApi.preview) and the
// document's contentType / fileExtension to pick the renderer.
function DocumentFilePreview({ document: doc }: { document: DocumentDto }) {
  const preview = useQuery({
    queryKey: ["dms", "preview", doc.id],
    queryFn: () => documentsApi.preview(doc.id),
  });
  const url = preview.data?.sasUrl ?? null;

  // The preview endpoint converts in-scope Office files to PDF and reports it
  // via `isPdf`; that signal is authoritative over the stored content type.
  // resolvePreviewKind reads the extension off the passed name, so prefer the
  // explicit fileExtension and fall back to the full file name.
  const kind = resolvePreviewKind({
    contentType: doc.contentType,
    fileName: doc.fileExtension || doc.fileName,
    isPdf: preview.data?.isPdf,
  });
  const isPdf = kind === "pdf";
  const isImage = kind === "image";
  const isText = kind === "text";
  const isOffice = kind === "office";

  const wrapper =
    "mb-space-3 rounded-md border border-border-light bg-warm-white";

  if (preview.isLoading) {
    return (
      <div className={`${wrapper} p-space-3 text-small text-gray-mid`}>
        Preparing preview…
      </div>
    );
  }
  if (!url) {
    return (
      <div className={`${wrapper} p-space-3 text-small text-gray-mid`}>
        Preview unavailable.
      </div>
    );
  }

  if (isPdf) {
    return (
      <iframe
        src={url}
        title={`Preview of ${doc.title}`}
        className={`${wrapper} h-[400px] w-full overflow-auto bg-white-pure`}
      />
    );
  }
  if (isImage) {
    return (
      <div className={`${wrapper} p-space-2`}>
        <img
          src={url}
          alt={`Preview of ${doc.title}`}
          className="mx-auto max-h-[400px] w-auto object-contain"
        />
      </div>
    );
  }
  if (isText) {
    return (
      <div className={wrapper}>
        <TextFilePreview url={url} />
      </div>
    );
  }
  if (isOffice) {
    return (
      <div className={`${wrapper} p-space-3 flex flex-col items-start gap-space-2`}>
        <span className="text-small text-gray-mid">
          Office documents — open in a new tab.
        </span>
        <PreviewActions url={url} />
      </div>
    );
  }
  return (
    <div className={`${wrapper} p-space-3 flex flex-col items-start gap-space-2`}>
      <span className="text-small text-gray-mid">
        Preview not available for this file type.
      </span>
      <PreviewActions url={url} />
    </div>
  );
}

// Download / Open-in-new-tab pair used by the non-renderable preview
// fallbacks. The signed URL is already resolved by the time it's shown.
function PreviewActions({ url }: { url: string }) {
  return (
    <div className="flex flex-wrap gap-space-2">
      <Button
        size="sm"
        variant="secondary"
        onClick={() => window.open(url, "_blank", "noopener")}
      >
        <Download size={14} aria-hidden /> Download
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => window.open(url, "_blank", "noopener")}
      >
        <ExternalLink size={14} aria-hidden /> Open in new tab
      </Button>
    </div>
  );
}

// Fetches a text file from the signed URL and renders the first 200 lines.
function TextFilePreview({ url }: { url: string }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setFailed(false);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((body) => {
        if (cancelled) return;
        setText(body.split("\n").slice(0, 200).join("\n"));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) {
    return (
      <div className="p-space-3 flex flex-col items-start gap-space-2">
        <span className="text-small text-gray-mid">
          Preview not available for this file type.
        </span>
        <PreviewActions url={url} />
      </div>
    );
  }
  if (text === null) {
    return <div className="p-space-3 text-small text-gray-mid">Loading preview…</div>;
  }
  return (
    <pre className="max-h-[400px] overflow-auto p-space-3 text-caption text-onyx whitespace-pre-wrap break-words m-0">
      {text}
    </pre>
  );
}

function ArchivedBadge() {
  return (
    <span
      className="inline-flex items-center gap-space-1 px-space-2 py-[2px] rounded-full text-caption font-display font-semibold uppercase tracking-label border"
      // Same token treatment as DocumentList's ArchivedPill — gold/amber
      // muted, with a 1px border so it reads even on white.
      style={{
        backgroundColor: "rgba(201, 169, 97, 0.20)",
        borderColor: "rgba(168, 136, 75, 0.50)",
        color: "#8B6F2E",
      }}
    >
      <Archive size={11} aria-hidden /> Archived
    </span>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
